/**
 * Iter51 Etapa 11 — Admin Energy Suppliers CRUD page.
 *
 * Allows admin to:
 *   - View all suppliers (active + inactive)
 *   - Filter by commodity (electric/gas/both)
 *   - Search by name or alias
 *   - Create new supplier
 *   - Edit existing (inline dialog)
 *   - Soft delete (set active=false)
 *   - Bulk seed Romanian suppliers
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Building2, Plus, RefreshCw, Trash2, Edit, Search, Zap, Flame, Globe,
  Sparkles,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SUPPLIER = {
  name: "", supplier_type: "both", aliases: [], website: "",
  anre_license: "", active: true, notes: "",
};

const TypeBadge = ({ type }) => {
  if (type === "electric") return <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 border text-xs"><Zap className="h-3 w-3 mr-1" />Electric</Badge>;
  if (type === "gas") return <Badge className="bg-orange-50 text-orange-700 border-orange-200 border text-xs"><Flame className="h-3 w-3 mr-1" />Gaz</Badge>;
  return <Badge className="bg-purple-50 text-purple-700 border-purple-200 border text-xs">Ambele</Badge>;
};

const EnergySuppliersAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [commodity, setCommodity] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [draft, setDraft] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { active_only: !showInactive, limit: 500 };
      if (search.trim()) params.q = search.trim();
      if (commodity !== "all") params.commodity = commodity;
      const r = await axios.get(`${API_URL}/energy-suppliers`, { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchItems, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, commodity, showInactive]);

  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_SUPPLIER);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setDraft({
      name: s.name || "",
      supplier_type: s.supplier_type || "both",
      aliases: s.aliases || [],
      website: s.website || "",
      anre_license: s.anre_license || "",
      active: s.active,
      notes: s.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      // aliases is a comma-separated string in UI; normalize back to array
      if (typeof payload.aliases === "string") {
        payload.aliases = payload.aliases.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (editing) {
        await axios.put(`${API_URL}/admin/energy-suppliers/${editing.id}`, payload);
        toast.success("Furnizor actualizat");
      } else {
        await axios.post(`${API_URL}/admin/energy-suppliers`, payload);
        toast.success("Furnizor creat");
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Marchezi „${s.name}" ca inactiv?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/energy-suppliers/${s.id}`);
      toast.success("Marcat inactiv");
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Importă lista de furnizori RO (ANRE)? Va sări peste cei existenți.")) return;
    try {
      const r = await axios.post(`${API_URL}/admin/energy-suppliers/seed`);
      toast.success(`Seed OK: ${r.data.created} create, ${r.data.skipped} existente. Total: ${r.data.total_in_db}`);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare seed");
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="energy-suppliers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Furnizori energie (RO)
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Catalog folosit la dropdown-ul „Furnizor anterior" pe Vânzări. {items.length} {showInactive ? "total" : "active"}.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleSeed} data-testid="seed-suppliers-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Importă seed RO (ANRE)
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="new-supplier-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Furnizor nou
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              placeholder="Caută după nume sau alias..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm flex-1"
              data-testid="suppliers-search-input"
            />
          </div>
          <Select value={commodity} onValueChange={setCommodity}>
            <SelectTrigger className="h-8 w-40 text-sm" data-testid="commodity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate tipurile</SelectItem>
              <SelectItem value="electric">Electric</SelectItem>
              <SelectItem value="gas">Gaz</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="show-inactive-toggle"
            />
            Include inactivi
          </label>
          <Button variant="outline" size="sm" onClick={fetchItems} disabled={loading} data-testid="refresh-suppliers-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardContent>
      </Card>

      {/* Suppliers list */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left py-2.5 px-4 font-medium">Furnizor</th>
                <th className="text-left py-2.5 px-3 font-medium">Tip</th>
                <th className="text-left py-2.5 px-3 font-medium">Aliase</th>
                <th className="text-left py-2.5 px-3 font-medium">Website</th>
                <th className="text-center py-2.5 px-3 font-medium">Activ</th>
                <th className="text-right py-2.5 px-4 font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400 italic">Niciun furnizor găsit</td></tr>
              )}
              {items.map((s) => (
                <tr key={s.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!s.active ? "opacity-50" : ""}`} data-testid={`supplier-row-${s.id}`}>
                  <td className="py-2.5 px-4">
                    <p className="font-medium text-slate-900">{s.name}</p>
                    {s.notes && <p className="text-xs text-slate-400 mt-0.5">{s.notes}</p>}
                  </td>
                  <td className="py-2.5 px-3"><TypeBadge type={s.supplier_type} /></td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">
                    {(s.aliases || []).slice(0, 3).join(", ")}
                    {(s.aliases || []).length > 3 && <span className="text-slate-400"> +{s.aliases.length - 3}</span>}
                  </td>
                  <td className="py-2.5 px-3">
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        <Globe className="h-3 w-3" /> {new URL(s.website).hostname}
                      </a>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {s.active
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-xs">Activ</Badge>
                      : <Badge variant="outline" className="text-xs">Inactiv</Badge>}
                  </td>
                  <td className="py-2.5 px-4 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    {s.active && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} className="text-red-600 hover:bg-red-50" data-testid={`delete-supplier-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `Editează: ${editing.name}` : "Furnizor nou"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Nume *</label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Ex: PPC Energie S.A." data-testid="dialog-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Tip *</label>
              <Select value={draft.supplier_type} onValueChange={(v) => setDraft({ ...draft, supplier_type: v })}>
                <SelectTrigger data-testid="dialog-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="gas">Gaz</SelectItem>
                  <SelectItem value="both">Ambele</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Aliase (separate prin virgulă)</label>
              <Input
                value={Array.isArray(draft.aliases) ? draft.aliases.join(", ") : draft.aliases}
                onChange={(e) => setDraft({ ...draft, aliases: e.target.value })}
                placeholder="Ex: Enel, PPC, PPC Energie"
                data-testid="dialog-aliases-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">Folosite la matching cu denumirile vechi din importuri SF/Excel</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Website</label>
              <Input value={draft.website || ""} onChange={(e) => setDraft({ ...draft, website: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
              <Textarea rows={2} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
              Activ
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Anulează</Button>
            <Button onClick={handleSave} disabled={saving || !draft.name.trim()} data-testid="dialog-save-btn">
              {saving ? "Se salvează..." : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnergySuppliersAdmin;
